import { Flex } from '@chakra-ui/react';
import { EmailContentForm } from 'src/components/EmailModule';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { Overlay } from 'src/components/Overlay';
import { Button, useToast } from '@morressier/alembic';
import { useSendAdhocEmail } from 'src/api/commands';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import ComposeReviewerInvitation from '../ReviewTab/InviteReviewer/ComposeReviewerInvitation';
import { normalizeRecipients } from '../../../utils/normalizeEmailRecipients';
import { emailContentSchema } from '@publishing-engine/domain/validation';
import { zodResolver } from '@hookform/resolvers/zod';
import { getAllTemplatesByCommand } from '../../../api/email-service';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

interface Props {
  setOpenSendEmailModal: (data: AdHocModalOpen) => void;
  email: string[];
}
export interface AdHocModalOpen {
  status: boolean;
  defaultEmail: string[];
}
const ComposeEmailModal = ({ setOpenSendEmailModal, email }: Props) => {
  const toast = useToast();
  const { submissionId, submissionContextId, organizationId } =
    useSubmissionData();

  const emailContentMethods = useForm<EmailContentForm>({
    defaultValues: {
      emailContent: {
        body: '',
        subject: '',
        recipients: { to: [{ text: email.join(',') }], cc: [], bcc: [] },
        attachments: []
      }
    },
    resolver: zodResolver(emailContentSchema)
  });

  const emailAttachments = emailContentMethods.watch(
    'emailContent.attachments'
  );

  const isUploadingAttachments = useMemo(() => {
    return !!emailAttachments?.some(att => att.isUploading);
  }, [emailAttachments]);

  const adhocEmailSendKey = ['send-adhoc-email', organizationId];

  const sendAdhocEmailMutation = useSendAdhocEmail(
    organizationId,
    adhocEmailSendKey,
    submissionId,
    submissionContextId
  );

  const { data: emailTemplates, isLoading: isLoadingTemplates } = useQuery({
    queryKey: [
      'browse-email-templates',
      organizationId,
      'SendAdhocEmail',
      submissionContextId
    ],
    queryFn: () =>
      organizationId
        ? getAllTemplatesByCommand({
            organizationId,
            commandName: 'SendAdhocEmail',
            query: { submissionContextId }
          })
        : null,
    enabled: !!organizationId
  });

  const { reset, handleSubmit } = emailContentMethods;
  const onSubmit: SubmitHandler<EmailContentForm> = async data => {
    const { emailContent } = data;
    const emailPayload = {
      ...emailContent,
      recipients: normalizeRecipients(emailContent.recipients),
      attachments: emailContent.attachments || []
    };
    try {
      await sendAdhocEmailMutation.mutateAsync({
        email: emailPayload
      });
      toast({
        variant: 'success',
        title: 'Email successfully sent.',
        status: 'success'
      });
      setOpenSendEmailModal({
        status: false,
        defaultEmail: []
      });
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Failed to send the email.',
        status: 'error'
      });
    }
  };
  return (
    <>
      <FormProvider {...emailContentMethods}>
        <form>
          <Overlay>
            <ComposeReviewerInvitation
              currentStep={0}
              totalSteps={0}
              emails={email}
              attachments={[]}
              emailTemplates={emailTemplates?.data}
              isLoadingTemplates={isLoadingTemplates}
              footer={
                <Flex width="100%" justifyContent="space-between">
                  <Button
                    variant="caution"
                    onClick={() => {
                      setOpenSendEmailModal({
                        status: false,
                        defaultEmail: []
                      });
                      reset();
                    }}
                  >
                    Exit
                  </Button>

                  <Flex gap={4}>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={isUploadingAttachments}
                      onClick={handleSubmit(onSubmit)}
                    >
                      Send Email
                    </Button>
                  </Flex>
                </Flex>
              }
              adHocEmail={true}
              commandName="SendAdhocEmail"
            />
          </Overlay>
        </form>
      </FormProvider>
    </>
  );
};

export default ComposeEmailModal;
